import type { IChatMessage } from "@/types/(my-space)/chat.interface";

type Pinnable = Pick<IChatMessage, "id" | "pinnedAt" | "pinnedBy" | "isDeleted">;

/**
 * Loaded messages with their pins matched to the chat's pinned list (the
 * server's answer to a pin): pinned ones take its `pinnedAt`, any other loses
 * its pin. Untouched messages keep their identity; the same list if nothing changed.
 */
export const withPins = <M extends Pinnable>(messages: M[], pinned: Pinnable[]): M[] => {
  const byId = new Map(pinned.map((p) => [p.id, p]));
  let changed = false;
  const next = messages.map((m) => {
    if (m.id <= 0) return m;
    const pin = byId.get(m.id);
    const pinnedAt = pin?.pinnedAt ?? null;
    const pinnedBy = pin?.pinnedBy ?? null;
    if (m.pinnedAt === pinnedAt && m.pinnedBy === pinnedBy) return m;
    changed = true;
    return { ...m, pinnedAt, pinnedBy };
  });
  return changed ? next : messages;
};

/**
 * What the pinned bar shows, the latest pin first: the chat's pinned list, each
 * swapped for its loaded copy when there is one (so an edit shows at once),
 * minus any that a live update has since unpinned or deleted.
 */
export const pinnedBarItems = <M extends Pinnable>(
  pinned: M[] | null | undefined,
  loaded: readonly M[],
): M[] => {
  if (!pinned?.length) return [];
  const byId = new Map(loaded.map((m) => [m.id, m]));
  return pinned
    .map((p) => byId.get(p.id) ?? p)
    .filter((m) => !!m.pinnedAt && !m.isDeleted);
};

/** The bar shows one pin at a time; a click shows it and moves to the next (older), wrapping. */
export const nextPinIndex = (index: number, count: number) =>
  count ? (index + 1) % count : 0;

/**
 * The chat list with my pinned chats (they come first) in [order] — a drag
 * just dropped, shown until the list refetches. Unknown ids keep their place.
 */
export const withPinnedOrder = <C extends { id: number; isPinned: boolean }>(
  conversations: C[],
  order: number[] | null,
): C[] => {
  if (!order) return conversations;
  const rank = new Map(order.map((id, i) => [id, i]));
  const pinned = conversations.filter((c) => c.isPinned);
  const sorted = [...pinned].sort(
    (a, b) => (rank.get(a.id) ?? Infinity) - (rank.get(b.id) ?? Infinity),
  );
  return [...sorted, ...conversations.filter((c) => !c.isPinned)];
};
